import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Alert,
  AlertTitle
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AuthenticationFlowsComponent from './AuthenticationFlowsComponent';
import ApiPermissionsScenario from './scenarios/ApiPermissionsScenario'; 
import PrerequisitesSetupComponent from './PrerequisitesSetupComponent';
import EmailIdTokenScenario from './scenarios/EmailIdTokenScenario';
import OptionalClaimsApiScenario from './scenarios/OptionalClaimsApiScenario';
import AppRolesScenario from './scenarios/AppRolesScenario';
import ProvisionUsersScenario from './scenarios/ProvisionUsersScenario';

const TestScenariosTab: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  
  // Define scenario categories and their scenarios
  const scenarioCategories = [
    {
      title: "Token Configuration",
      icon: <VpnKeyIcon />,
      color: "secondary.main",
      scenarios: [
        {
          id: "email-id-token",
          title: "Add Email Claim to ID Token",
          description: "Configure your application to include the email claim in ID tokens",
          level: "Beginner"
        },
        {
          id: "optional-claims-api",
          title: "Configure and Test Optional Claims for APIs",
          description: "Add tenant ID, UPN, and other claims to access tokens for APIs",
          level: "Intermediate"
        },
        {
          id: "claims-mapping-policy",
          title: "Test Claims Mapping Policy",
          description: "Create and apply a claims mapping policy to add custom claims to tokens",
          level: "Advanced"
        }
      ]
    },
    {
      title: "Authorization",
      icon: <SecurityIcon />,
      color: "success.main",
      scenarios: [
        {
          id: "app-roles",
          title: "Create and Test App Roles",
          description: "Define app roles, assign users, and verify role claims in tokens",
          level: "Intermediate"
        },
        {
          id: "oauth-scopes",
          title: "Configure and Test OAuth Scopes",
          description: "Create custom scopes, obtain consent, and validate access tokens",
          level: "Intermediate"
        },
        {
          id: "api-permissions",
          title: "Configure API Permissions",
          description: "Add and test various API permissions (delegated vs. application)",
          level: "Beginner"
        }
      ]
    },
    {
      title: "User Management",
      icon: <PersonAddIcon />,
      color: "primary.main",
      scenarios: [
        {
          id: "provision-users",
          title: "Provision Users to an Application",
          description: "Create users and provision them to an application",
          level: "Beginner"
        }
      ]
    },
    {
      title: "Authentication Flows",
      icon: <BadgeIcon />,
      color: "warning.main",
      scenarios: [
        {
          id: "authentication-flows",
          title: "OAuth Flow Testing",
          description: "Test Authorization Code, Client Credentials, Hybrid, and Implicit flows using AppConfig²'s OAuth Flow Tester",
          level: "Advanced"
        }
      ]
    },
    {
      title: "Advanced Scenarios",
      icon: <SettingsIcon />,
      color: "info.main",
      scenarios: [
        {
          id: "directory-extensions",
          title: "Test with Custom Directory Extensions",
          description: "Create directory extensions and verify they appear in tokens",
          level: "Advanced"
        }
      ]
    }
  ];

  const prerequisiteScenario = {
    id: "prerequisites-setup",
    title: "Essential Setup Guide",
    description: "Configure Web Apps and SPAs for authentication testing in AppConfig²"
  };

  const [showAuthFlows, setShowAuthFlows] = useState(false);
  
  // Render specific scenario content based on selection
  const renderScenarioContent = () => {
    // Handle email claim to ID token scenario
    if (selectedScenario === "email-id-token") {
      return <EmailIdTokenScenario onBack={() => setSelectedScenario(null)} />;
    }

    // Update the optional claims for APIs scenario
    if (selectedScenario === "optional-claims-api") {
      return <OptionalClaimsApiScenario onBack={() => setSelectedScenario(null)} />;
    }

    if (selectedScenario === "claims-mapping-policy") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Test Claims Mapping Policy
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Complete Workflow Available</AlertTitle>
            <Typography variant="body2">
              We've created a comprehensive step-by-step workflow for implementing claims mapping policies 
              that includes directory extensions, policy creation, application assignment, and testing.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Claims mapping policies allow you to customize the claims that appear in tokens issued to your applications. 
            This is particularly useful when you need to include custom attributes from your directory or 
            transform existing claims.
          </Typography>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
              What You'll Learn
            </Typography>
            <Typography variant="body2">
              The complete workflow covers identifying directory extensions, creating claims mapping policies, 
              assigning them to applications, enabling acceptMappedClaims, and verifying the results in tokens.
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ContentCopyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Step-by-Step Claims Mapping Workflow
                </Typography>
              </Box>
              
              <Typography variant="body2" paragraph>
                Follow our detailed workflow that guides you through the entire process:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Step 1: Identify Directory Extensions" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Step 2: Create Claims Mapping Policy" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Step 3: Assign Policy to Application" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Step 4: Enable acceptMappedClaims" />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Step 5: Test and Verify Custom Claims" />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 3 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<ArrowRightIcon />}
                  onClick={() => {
                    // Scroll to the workflows section within the same page
                    const workflowElement = document.getElementById('workflow-custom-claims');
                    if (workflowElement) {
                      workflowElement.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      // If the element isn't found, provide user feedback
                      alert('Please navigate to the Workflows tab to view the complete workflow.');
                    }
                  }}
                >
                  Go to Complete Workflow
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Testing Steps (After Completing Workflow)
            </Typography>
            <Typography variant="body2" paragraph>
              Once you've completed the workflow, you can quickly test your claims mapping policy:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Use App Tester to authenticate and request tokens"
                  secondary="Navigate to your application and use the 'Fetch Access Token' feature"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Verify custom claims in decoded tokens"
                  secondary="Check both ID tokens and access tokens for your custom claims"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Use JWT Token Decoder tool for detailed analysis"
                  secondary="Copy tokens and use the decoder tool for comprehensive claim analysis"
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowRightIcon />}
              onClick={() => setSelectedScenario(null)}
            >
              Back to All Scenarios
            </Button>
          </Box>
        </Box>
      );
    }   
    
    if (selectedScenario === "app-roles") {
      return <AppRolesScenario onBack={() => setSelectedScenario(null)} />;
    }

    if (selectedScenario === "oauth-scopes") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Configure and Test OAuth Scopes
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Complete API Workflow Available</AlertTitle>
            <Typography variant="body2">
              OAuth scopes are covered comprehensively in our "Exposing & Consuming APIs" workflow, 
              which includes scope creation, client configuration, and testing with real applications.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            OAuth scopes are the mechanism that allows applications to request specific permissions 
            when accessing APIs. They define what resources and operations a client application can 
            access on behalf of a user or using its own identity.
          </Typography>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
              What You'll Learn About Scopes
            </Typography>
            <Typography variant="body2">
              The complete workflow covers creating custom scopes in your API applications, 
              configuring client applications to request those scopes, handling consent, 
              and validating scope claims in access tokens.
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Exposing & Consuming APIs Workflow
                </Typography>
              </Box>
              
              <Typography variant="body2" paragraph>
                This comprehensive workflow covers all aspects of OAuth scopes:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 1: Create and Expose API Scopes" 
                    secondary="Define custom scopes in your API application with proper naming and permissions"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 2: Configure Client Applications" 
                    secondary="Add API permissions to client applications to request your custom scopes"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 3: Handle Admin Consent" 
                    secondary="Understand when admin consent is required and how to grant it"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 4: Test End-to-End" 
                    secondary="Use App Tester to authenticate and validate that scope claims appear in access tokens"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 5: Validate Token Claims" 
                    secondary="Verify that access tokens contain the correct 'scp' (scope) claims"
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 3 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<ArrowRightIcon />}
                  onClick={() => {
                    // Scroll to the API workflow section
                    const workflowElement = document.getElementById('workflow-expose-api');
                    if (workflowElement) {
                      workflowElement.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      // If the element isn't found, provide user feedback
                      alert('Please navigate to the Workflows tab to view the complete API workflow.');
                    }
                  }}
                >
                  Go to Complete Workflow
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              OAuth Scope Examples You'll Create
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Read Access Scope" 
                  secondary="api://your-api-id/data.read → Allows reading application data"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Write Access Scope" 
                  secondary="api://your-api-id/data.write → Allows creating/updating data"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Admin Access Scope" 
                  secondary="api://your-api-id/admin → Allows administrative operations"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="User Impersonation Scope" 
                  secondary="api://your-api-id/user_impersonation → Allows acting on behalf of signed-in user"
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Key OAuth Scope Concepts
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Delegated vs Application Permissions" 
                  secondary="Scopes are used for delegated permissions (acting on behalf of a user)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Consent Requirements" 
                  secondary="Users or admins must consent to scopes before applications can use them"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Token Claims" 
                  secondary="Granted scopes appear as 'scp' claims in access tokens"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="API Audience" 
                  secondary="Scopes are specific to the API (audience) for which tokens are requested"
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Testing Overview (From the Workflow)
            </Typography>
            <Typography variant="body2" paragraph>
              After completing the full workflow, you'll be able to:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Use App Tester to request tokens with specific scopes"
                  secondary="Select your custom API and specific scopes in the authentication flow"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Verify scope claims in access tokens"
                  secondary="Check that the 'scp' claim contains exactly the scopes you requested"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Test different scope combinations"
                  secondary="Request different combinations of scopes to test your API's authorization logic"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Validate consent flows"
                  secondary="Experience the consent process from both user and admin perspectives"
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowRightIcon />}
              onClick={() => setSelectedScenario(null)}
            >
              Back to All Scenarios
            </Button>
          </Box>
        </Box>
      );
    }  

    if (selectedScenario === "api-permissions") {
      return <ApiPermissionsScenario onBack={() => setSelectedScenario(null)} />;
    }    

    if (selectedScenario === "prerequisites-setup") {
      return <PrerequisitesSetupComponent onBack={() => setSelectedScenario(null)} />;
    }

    if (selectedScenario === "directory-extensions") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Test with Custom Directory Extensions
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>This Scenario Uses Claims Mapping Policy</AlertTitle>
            <Typography variant="body2">
              Testing custom directory extensions in tokens requires creating and applying a claims mapping policy. 
              This is the same process covered in our Claims Mapping Policy scenario.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Custom directory extensions (like those synced from on-premises Active Directory or created in Entra ID) 
            need to be explicitly mapped to token claims using claims mapping policies. This ensures your applications 
            can access these custom attributes through standard token claims.
          </Typography>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
              Why Claims Mapping is Required
            </Typography>
            <Typography variant="body2">
              Directory extensions don't automatically appear in tokens. You must create a claims mapping policy 
              that tells Microsoft Entra ID which extensions to include and what to name them in the token. 
              This provides security and control over which custom attributes are exposed to applications.
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ContentCopyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Complete Claims Mapping Workflow
                </Typography>
              </Box>
              
              <Typography variant="body2" paragraph>
                The Claims Mapping Policy workflow covers everything you need for directory extensions:
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 1: Identify Directory Extensions" 
                    secondary="Find and note the exact names of your directory extensions"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 2: Create Claims Mapping Policy" 
                    secondary="Map directory extensions to custom claims in your tokens"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 3: Assign Policy to Application" 
                    secondary="Apply the policy to applications that need these custom claims"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 4: Enable acceptMappedClaims" 
                    secondary="Configure applications to accept the mapped claims"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Step 5: Test and Verify Custom Claims" 
                    secondary="Validate that directory extensions appear correctly in tokens"
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<ArrowRightIcon />}
                  onClick={() => setSelectedScenario("claims-mapping-policy")}
                >
                  Go to Claims Mapping Policy
                </Button>
                
                <Button 
                  variant="outlined"
                  startIcon={<ArrowRightIcon />}
                  onClick={() => {
                    // Navigate to workflows section
                    const event = new CustomEvent('navigateToWorkflow', {
                      detail: { tab: 5, workflow: 'custom-claims' }
                    });
                    window.dispatchEvent(event);
                  }}
                >
                  Go to Complete Workflow
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Common Directory Extension Examples
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Employee ID from AD" 
                  secondary="extension_ab123cde45678f_employeeId → 'employee_id' claim"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Department Information" 
                  secondary="extension_ab123cde45678f_department → 'department' claim"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Cost Center" 
                  secondary="extension_ab123cde45678f_costCenter → 'cost_center' claim"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText 
                  primary="Custom Business Attributes" 
                  secondary="Any custom attributes synced from your on-premises directory"
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Key Considerations for Directory Extensions
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Extension Compatibility" 
                  secondary="Not all AD attributes can be used in claims mapping. extensionAttribute1-15 work best."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Data Privacy" 
                  secondary="Only include directory extensions that are safe to expose to applications"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Sync Requirements" 
                  secondary="Extensions must be actively synced from AD or created in Entra ID to appear in tokens"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText 
                  primary="Token Size Impact" 
                  secondary="Adding many extensions can increase token size; be selective about which ones to include"
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowRightIcon />}
              onClick={() => setSelectedScenario(null)}
            >
              Back to All Scenarios
            </Button>
          </Box>
        </Box>
      );
    }    

    if (selectedScenario === "authentication-flows") {
      return <AuthenticationFlowsComponent onBack={() => setSelectedScenario(null)} />;
    }   

    if (selectedScenario === "provision-users") {
      return <ProvisionUsersScenario onBack={() => setSelectedScenario(null)} />;
    }      
    
    // Default: return a message that the scenario content is under development
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          This scenario is currently under development
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Detailed steps for this test scenario will be available soon.
        </Typography>
        <Button 
          variant="outlined"
          onClick={() => setSelectedScenario(null)}
        >
          Back to All Scenarios
        </Button>
      </Box>
    );
  };
  
  // If a scenario is selected, show its content
  if (selectedScenario) {
    return renderScenarioContent();
  }
  
  // Otherwise, show the list of scenario categories and their scenarios
  return (
    <Box>
      {/* Introduction Section - Similar to AppTesterDocumentation */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'primary.lighter', borderLeft: '5px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PlayArrowIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
            Test Scenarios
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Hands-on <strong>test scenarios</strong> provide step-by-step instructions for configuring and testing 
          specific Microsoft Entra ID features using AppConfig²'s integrated tools and interfaces.
        </Typography>
        <Typography variant="body2" color="text.primary">
          Each scenario includes detailed instructions, expected outcomes, and troubleshooting tips. 
          Start with the Prerequisites & Setup to ensure your environment is properly configured.
        </Typography>
      </Paper>

      {/* Prerequisites Section - Distinguished with warning styling */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: '#fff3e0', borderLeft: '5px solid', borderColor: 'warning.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WarningIcon sx={{ fontSize: '2rem', mr: 1.5, color: 'warning.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
            Prerequisites & Setup
          </Typography>
          <Chip label="Start Here" color="warning" variant="outlined" size="small" sx={{ ml: 2 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
          Before proceeding with any test scenarios, complete the essential setup to ensure your environment 
          is properly configured for authentication testing.
        </Typography>
        
        <Card 
          variant="outlined"
          sx={{ 
            cursor: 'pointer',
            borderColor: 'warning.main',
            backgroundColor: '#fffbf0',
            '&:hover': {
              boxShadow: 3,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s',
              backgroundColor: '#fff8e1'
            }
          }}
          onClick={() => setSelectedScenario(prerequisiteScenario.id)}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <SettingsIcon sx={{ color: 'warning.main', mr: 1 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'warning.dark' }}>
                {prerequisiteScenario.title}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              ⚠️ REQUIRED: {prerequisiteScenario.description}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Chip label="Essential" color="error" size="small" />
              <Chip label="Setup Guide" color="warning" variant="outlined" size="small" sx={{ ml: 1 }} />
            </Box>
          </CardContent>
        </Card>
      </Paper>

      {/* Test Scenarios Categories */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3, mb: 2, borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
        Test Scenario Categories
      </Typography>
      
      {scenarioCategories.map((category, index) => (
        <Box key={index} sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {React.cloneElement(category.icon, { sx: { mr: 1.5, color: category.color, fontSize: '1.5rem' } })}
            <Typography variant="h6" sx={{ fontWeight: 500, color: category.color }}>
              {category.title}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {category.scenarios.map((scenario) => (
              <Grid item xs={12} md={6} key={scenario.id}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s'
                    }
                  }}
                  onClick={() => setSelectedScenario(scenario.id)}
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                      {scenario.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
                      {scenario.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Chip 
                        label={scenario.level} 
                        size="small" 
                        color={
                          scenario.level === 'Beginner' ? 'success' :
                          scenario.level === 'Intermediate' ? 'warning' : 'error'
                        }
                        variant="outlined"
                      />
                      <ArrowRightIcon color="action" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Information Note */}
      <Paper elevation={0} sx={{ p: 3, mt: 4, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <InfoOutlinedIcon color="info" sx={{ mr: 1 }} />
          <Typography variant="h6" color="info.dark" sx={{ fontWeight: 500 }}>Getting Started</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Each scenario includes step-by-step instructions, configuration examples, and verification steps. 
          Scenarios marked as "Advanced" may require additional Microsoft Entra ID permissions or PowerShell knowledge.
          Always test configurations in a non-production environment first.
        </Typography>
      </Paper>
    </Box>
  );
};

export default TestScenariosTab;